import { useState, useEffect, useCallback } from 'react';
import { getSuggestions } from '../services/getSuggestions';

/**
 * Custom hook para manejo inteligente de sugerencias de búsqueda
 * 
 * @description Encapsula toda la lógica de estado y efectos para el sistema de sugerencias
 * de búsqueda. Implementa validaciones defensivas, manejo automático de errores,
 * optimizaciones de rendimiento y cleanup automático de estados. Diseñado para ser
 * reutilizable en múltiples componentes que requieran funcionalidad de autocompletado.
 * 
 * **Características principales:**
 * - Validación automática de longitud mínima (3 caracteres)
 * - Manejo robusto de errores con fallbacks seguros
 * - Reset automático cuando el input pierde el foco
 * - Optimización de rendimiento con useCallback
 * - Integración transparente con sistema de tokens
 * - Estado de selección para navegación por teclado
 * 
 * **Validaciones implementadas:**
 * - Longitud mínima de término de búsqueda
 * - Estado de foco del input
 * - Manejo de respuestas HTTP inválidas
 * - Cleanup automático en pérdida de foco
 * 
 * **Optimizaciones de rendimiento:**
 * - useCallback para prevenir re-creaciones innecesarias
 * - Validación temprana para evitar peticiones innecesarias
 * - Dependencias mínimas y precisas en efectos
 * 
 * @hook
 * @param {string} palabra - Término de búsqueda ingresado por el usuario (mínimo 3 caracteres)
 * @param {string} category - Categoría seleccionada para filtrar sugerencias
 * @param {boolean} hasFocus - Estado de foco del input de búsqueda
 * 
 * @returns {Object} Objeto con estado y funciones de sugerencias
 * @returns {Array} returns.listaSugerencias - Array de objetos de sugerencias desde la API
 * @returns {number} returns.selectedIndex - Índice de la sugerencia seleccionada (-1 si ninguna)
 * @returns {Function} returns.setSelectedIndex - Función para actualizar el índice seleccionado
 * @returns {Function} returns.resetSuggestions - Función para limpiar todas las sugerencias
 * 
 * @example
 * // Uso básico en componente de búsqueda
 * const SearchComponent = () => {
 *   const [searchTerm, setSearchTerm] = useState('');
 *   const [category, setCategory] = useState('');
 *   const [hasFocus, setHasFocus] = useState(false);
 * 
 *   const {
 *     listaSugerencias,
 *     selectedIndex,
 *     setSelectedIndex,
 *     resetSuggestions
 *   } = useSuggestions(searchTerm, category, hasFocus);
 * 
 *   return (
 *     <div>
 *       <input
 *         value={searchTerm}
 *         onChange={(e) => setSearchTerm(e.target.value)}
 *         onFocus={() => setHasFocus(true)}
 *         onBlur={() => setHasFocus(false)}
 *       />
 *       {listaSugerencias.length > 0 && (
 *         <SuggestionsList 
 *           suggestions={listaSugerencias}
 *           selectedIndex={selectedIndex}
 *           onSelect={setSelectedIndex}
 *         />
 *       )}
 *     </div>
 *   );
 * };
 * 
 * @example
 * // Integración con navegación por teclado
 * const SearchWithKeyboard = () => {
 *   const { listaSugerencias, selectedIndex, setSelectedIndex } = 
 *     useSuggestions(query, category, focused);
 * 
 *   const handleKeyDown = (e) => {
 *     if (e.key === 'ArrowDown') {
 *       setSelectedIndex(prev => 
 *         Math.min(prev + 1, listaSugerencias.length - 1)
 *       );
 *     } else if (e.key === 'ArrowUp') {
 *       setSelectedIndex(prev => Math.max(prev - 1, 0));
 *     }
 *   };
 * 
 *   return <input onKeyDown={handleKeyDown} />;
 * };
 * 
 * @example
 * // Múltiples instancias independientes
 * const MultiSearchComponent = () => {
 *   const mainSearch = useSuggestions(mainQuery, 'news', mainFocused);
 *   const filterSearch = useSuggestions(filterQuery, 'sports', filterFocused);
 * 
 *   return (
 *     <div>
 *       <SearchInput suggestions={mainSearch} />
 *       <FilterInput suggestions={filterSearch} />
 *     </div>
 *   );
 * };
 * 
 * @example
 * // Manejo de reset manual
 * const SearchWithReset = () => {
 *   const { listaSugerencias, resetSuggestions } = 
 *     useSuggestions(query, category, hasFocus);
 * 
 *   const handleClear = () => {
 *     setQuery('');
 *     resetSuggestions(); // Limpia sugerencias manualmente
 *   };
 * 
 *   return (
 *     <div>
 *       <input value={query} />
 *       <button onClick={handleClear}>Limpiar</button>
 *     </div>
 *   );
 * };
 * 
 * @example
 * // Testing del hook
 * import { renderHook, act } from '@testing-library/react';
 * 
 * test('useSuggestions fetches data when conditions are met', async () => {
 *   const { result } = renderHook(() => 
 *     useSuggestions('test query', 'news', true)
 *   );
 * 
 *   await act(async () => {
 *     // Esperar a que se resuelvan las sugerencias
 *   });
 * 
 *   expect(result.current.listaSugerencias).toHaveLength(5);
 * });
 * 
 * @example
 * // Estructura de datos de sugerencias esperada:
 * const suggestionData = [
 *   {
 *     sugerencia: "término de búsqueda completo",
 *     relevancia: 0.95,
 *     categoria: "deportes"
 *   }
 * ];
 * 
 * @since 1.1.0
 * @author Valeria Garcia
 * 
 * @see {@link ../services/getSuggestions.js} Servicio de API utilizado internamente
 * @see {@link ../components/suggestions/suggestions.js} Componente que utiliza este hook
 * @see {@link ./useKeyboardNavigation.js} Hook complementario para navegación
 */
export const useSuggestions = (palabra, category, hasFocus) => {
  const [listaSugerencias, setListaSugerencias] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const fetchSuggestions = useCallback(async () => {
    if (!palabra || palabra.length < 3 || !hasFocus) {
      setListaSugerencias([]);
      return;
    }

    try {
      const sugerencia = await getSuggestions(palabra, category);
      
      if (sugerencia?.statusCode === 200) {
        setListaSugerencias(sugerencia.data);
      } else {
        setListaSugerencias([]);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setListaSugerencias([]);
    }
  }, [palabra, hasFocus, category]);

  const resetSuggestions = useCallback(() => {
    setListaSugerencias([]);
    setSelectedIndex(-1);
  }, []);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  useEffect(() => {
    if (!hasFocus) {
      resetSuggestions();
    }
  }, [hasFocus, resetSuggestions]);

  return {
    listaSugerencias,
    selectedIndex,
    setSelectedIndex,
    resetSuggestions
  };
};